import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Trophy,
  Calendar,
  Award,
  MapPin,
  ArrowUp,
  Activity,
  Cloud,
  CloudRain,
  Sun,
  TrendingUp,
  Users,
  FileText,
  Clock,
  ChevronRight,
  Download,
  BookOpen,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const API_BASE = "https://api.fsgu.co.za";

const AdminDashboard = () => {
  const { isDarkMode } = useTheme();
  const [stats, setStats] = useState({
    totalTournaments: 0,
    upcomingTournaments: 0,
    totalClubs: 45,
    totalResults: 0,
    oomSeasons: 0,
    oomPlayers: 0,
    drawsImported: 0,
  });
  const [loading, setLoading] = useState(true);
  const [weather, setWeather] = useState([]);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [upcomingTournaments, setUpcomingTournaments] = useState([]);
  const [recentResults, setRecentResults] = useState([]);

  const adminData = JSON.parse(sessionStorage.getItem("adminData") || "{}");

  useEffect(() => {
    fetchDashboardStats();
    fetchWeather();

    const handleScroll = () => {
      setShowBackToTop(window.pageYOffset > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDownloadGuide = () => {
    const link = document.createElement("a");
    link.href = "/FSGU_Admin_Guide.docx";
    link.download = "FSGU_Admin_Guide.docx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fetchWeather = async () => {
    setWeatherLoading(true);
    try {
      const locations = [
        { name: "Bloemfontein", lat: -29.1167, lon: 26.2167 },
        { name: "Welkom", lat: -27.9833, lon: 26.7333 },
        { name: "Bethlehem", lat: -28.2333, lon: 28.3167 },
        { name: "Kroonstad", lat: -27.65, lon: 27.2333 },
      ];

      const weatherPromises = locations.map(async (location) => {
        try {
          const response = await fetch(
            `https://api.open-meteo.com/v1/forecast?latitude=${location.lat}&longitude=${location.lon}&current_weather=true`
          );
          const data = await response.json();
          return {
            name: location.name,
            temp: Math.round(data.current_weather.temperature),
            windSpeed: Math.round(data.current_weather.windspeed),
            weatherCode: data.current_weather.weathercode,
          };
        } catch (error) {
          return { name: location.name, temp: "--", windSpeed: "--", weatherCode: 0 };
        }
      });

      const weatherData = await Promise.all(weatherPromises);
      setWeather(weatherData);
    } catch (error) {
      console.error("Error fetching weather:", error);
    } finally {
      setWeatherLoading(false);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      let totalTournaments = 0;
      let upcomingCount = 0;
      let totalResults = 0;
      let oomSeasons = 0;
      let oomPlayers = 0;

      // Fetch tournaments
      try {
        const response = await fetch(`${API_BASE}/tournaments-list.php?status=published`);
        const data = await response.json();
        if (data.success && data.stats) {
          totalTournaments = data.stats.total || 0;
          upcomingCount = data.stats.upcoming_count || 0;
        }
        // Get upcoming tournaments for list
        if (data.success && data.data) {
          const now = new Date();
          const upcoming = data.data
            .filter((t) => new Date(t.start_date) >= now)
            .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
            .slice(0, 5);
          setUpcomingTournaments(upcoming);
        }
      } catch (error) {
        console.log("Could not fetch tournaments:", error);
      }

      // Fetch results
      try {
        const response = await fetch(`${API_BASE}/get-results.php`);
        const data = await response.json();
        if (data.success && data.data) {
          totalResults = data.data.length;
          // Get recent results
          const recent = data.data
            .sort((a, b) => new Date(b.tournament_date) - new Date(a.tournament_date))
            .slice(0, 5);
          setRecentResults(recent);
        }
      } catch (error) {
        console.log("Could not fetch results:", error);
      }

      // Fetch OOM seasons
      try {
        const response = await fetch(`${API_BASE}/oom-seasons.php?status=active`);
        const data = await response.json();
        if (data.success && data.data) {
          oomSeasons = data.data.length;
        }
      } catch (error) {
        console.log("Could not fetch OOM seasons:", error);
      }

      // Fetch OOM players
      try {
        const response = await fetch(`${API_BASE}/oom-players.php?status=active&limit=1`);
        const data = await response.json();
        if (data.success) {
          oomPlayers = data.total || 0;
        }
      } catch (error) {
        console.log("Could not fetch OOM players:", error);
      }

      setStats({
        totalTournaments,
        upcomingTournaments: upcomingCount,
        totalClubs: 45,
        totalResults,
        oomSeasons,
        oomPlayers,
      });

      setLoading(false);
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
      setLoading(false);
    }
  };

  const getWeatherIcon = (code) => {
    if (code === 0) return Sun;
    if (code <= 3) return Cloud;
    if (code >= 51) return CloudRain;
    return Cloud;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "TBA";
    return new Date(dateString).toLocaleDateString("en-ZA", {
      day: "numeric",
      month: "short",
    });
  };

  // Quick Stats
  const quickStats = [
    {
      label: "Tournaments",
      value: stats.totalTournaments,
      subtext: `${stats.upcomingTournaments} upcoming`,
      icon: Trophy,
      color: "green",
      link: "/admin/tournaments",
    },
    {
      label: "Results",
      value: stats.totalResults,
      subtext: "Published",
      icon: Award,
      color: "gold",
      link: "/admin/results",
    },
    {
      label: "OOM Seasons",
      value: stats.oomSeasons,
      subtext: `${stats.oomPlayers} players`,
      icon: TrendingUp,
      color: "blue",
      link: "/admin/oom",
    },
    {
      label: "Clubs",
      value: stats.totalClubs,
      subtext: "Affiliated",
      icon: MapPin,
      color: "purple",
      link: "/admin/clubs",
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Activity
            className={`w-12 h-12 mx-auto mb-4 animate-spin ${
              isDarkMode ? "text-fsgu-green-400" : "text-fsgu-green-500"
            }`}
          />
          <p className={isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-600"}>
            Loading dashboard...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Row: Welcome + Weather */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Welcome */}
        <div
          className={`p-4 rounded-xl border ${
            isDarkMode
              ? "bg-gradient-to-r from-fsgu-green-900/20 to-fsgu-blue-900/20 border-fsgu-gray-700"
              : "bg-gradient-to-r from-fsgu-green-50 to-fsgu-blue-50 border-fsgu-silver-200"
          }`}
        >
          <h1
            className={`text-xl font-bold mb-1 ${
              isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
            }`}
          >
            Welcome back, {adminData.display_name || "Admin"}!
          </h1>
          <p
            className={`text-xs ${
              isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-600"
            }`}
          >
            Here's what's happening with FSGU today
          </p>
        </div>

        {/* Weather */}
        <div
          className={`p-4 rounded-xl border ${
            isDarkMode
              ? "bg-fsgu-gray-800 border-fsgu-gray-700"
              : "bg-white border-fsgu-silver-200"
          }`}
        >
          <div className="flex items-center justify-between mb-3">
            <h3
              className={`text-sm font-semibold ${
                isDarkMode ? "text-fsgu-silver-300" : "text-fsgu-silver-700"
              }`}
            >
              Free State Weather
            </h3>
            <span
              className={`text-xs ${
                isDarkMode ? "text-fsgu-silver-500" : "text-fsgu-silver-500"
              }`}
            >
              Live
            </span>
          </div>

          {weatherLoading ? (
            <div className="flex items-center justify-center py-4">
              <Activity
                className={`w-5 h-5 animate-spin ${
                  isDarkMode ? "text-fsgu-green-400" : "text-fsgu-green-500"
                }`}
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              {weather.map((location, index) => {
                const WeatherIcon = getWeatherIcon(location.weatherCode);
                return (
                  <div
                    key={index}
                    className={`p-2 rounded-lg ${
                      isDarkMode ? "bg-fsgu-gray-700/50" : "bg-fsgu-silver-50"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span
                        className={`text-xs font-medium ${
                          isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-600"
                        }`}
                      >
                        {location.name}
                      </span>
                      <WeatherIcon
                        className={`w-4 h-4 ${
                          isDarkMode ? "text-fsgu-blue-400" : "text-fsgu-blue-500"
                        }`}
                      />
                    </div>
                    <div
                      className={`text-lg font-bold ${
                        isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
                      }`}
                    >
                      {location.temp}°C
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div
        className={`p-6 rounded-xl border ${
          isDarkMode
            ? "bg-fsgu-gray-800 border-fsgu-gray-700"
            : "bg-white border-fsgu-silver-200"
        }`}
      >
        <h3
          className={`text-lg font-semibold mb-4 ${
            isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
          }`}
        >
          Quick Actions
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <button
            onClick={handleDownloadGuide}
            className={`flex items-center gap-3 p-4 rounded-lg transition-all duration-200 hover:scale-[1.02] ${
              isDarkMode
                ? "bg-fsgu-gray-700/50 hover:bg-fsgu-gray-700 text-fsgu-silver-300 border border-fsgu-gray-600"
                : "bg-fsgu-silver-50 hover:bg-fsgu-silver-100 text-fsgu-silver-700 border border-fsgu-silver-200"
            }`}
          >
            <BookOpen className="w-5 h-5" />
            <div className="text-left">
              <div className="font-medium">How To Guide</div>
              <div className="text-xs opacity-75 flex items-center gap-1">
                <Download className="w-3 h-3" />
                Download
              </div>
            </div>
          </button>

          <Link
            to="/admin/tournaments/add"
            className={`flex items-center gap-3 p-4 rounded-lg transition-all duration-200 hover:scale-[1.02] ${
              isDarkMode
                ? "bg-fsgu-green-900/20 hover:bg-fsgu-green-900/30 text-fsgu-green-400 border border-fsgu-green-700/30"
                : "bg-fsgu-green-50 hover:bg-fsgu-green-100 text-fsgu-green-700 border border-fsgu-green-200"
            }`}
          >
            <Trophy className="w-5 h-5" />
            <div className="text-left">
              <div className="font-medium">Add Tournament</div>
              <div className="text-xs opacity-75">Create new event</div>
            </div>
          </Link>

          <Link
            to="/admin/results/add"
            className={`flex items-center gap-3 p-4 rounded-lg transition-all duration-200 hover:scale-[1.02] ${
              isDarkMode
                ? "bg-fsgu-gold-900/20 hover:bg-fsgu-gold-900/30 text-fsgu-gold-400 border border-fsgu-gold-700/30"
                : "bg-fsgu-gold-50 hover:bg-fsgu-gold-100 text-fsgu-gold-700 border border-fsgu-gold-200"
            }`}
          >
            <Award className="w-5 h-5" />
            <div className="text-left">
              <div className="font-medium">Add Result</div>
              <div className="text-xs opacity-75">Publish scores</div>
            </div>
          </Link>

          <Link
            to="/admin/oom"
            className={`flex items-center gap-3 p-4 rounded-lg transition-all duration-200 hover:scale-[1.02] ${
              isDarkMode
                ? "bg-fsgu-blue-900/20 hover:bg-fsgu-blue-900/30 text-fsgu-blue-400 border border-fsgu-blue-700/30"
                : "bg-fsgu-blue-50 hover:bg-fsgu-blue-100 text-fsgu-blue-700 border border-fsgu-blue-200"
            }`}
          >
            <TrendingUp className="w-5 h-5" />
            <div className="text-left">
              <div className="font-medium">Update OOM</div>
              <div className="text-xs opacity-75">Manage standings</div>
            </div>
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {quickStats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Link
              key={index}
              to={stat.link}
              className={`group p-5 rounded-xl border transition-all duration-300 hover:scale-[1.02] hover:shadow-lg ${
                isDarkMode
                  ? "bg-fsgu-gray-800 border-fsgu-gray-700 hover:border-fsgu-green-500/50"
                  : "bg-white border-fsgu-silver-200 hover:border-fsgu-green-500/50"
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div
                  className={`p-2.5 rounded-lg ${
                    stat.color === "green"
                      ? isDarkMode
                        ? "bg-fsgu-green-500/20"
                        : "bg-fsgu-green-500/10"
                      : stat.color === "blue"
                      ? isDarkMode
                        ? "bg-fsgu-blue-500/20"
                        : "bg-fsgu-blue-500/10"
                      : stat.color === "gold"
                      ? isDarkMode
                        ? "bg-fsgu-gold-500/20"
                        : "bg-fsgu-gold-500/10"
                      : isDarkMode
                      ? "bg-purple-500/20"
                      : "bg-purple-500/10"
                  }`}
                >
                  <Icon
                    className={`w-5 h-5 ${
                      stat.color === "green"
                        ? isDarkMode
                          ? "text-fsgu-green-400"
                          : "text-fsgu-green-600"
                        : stat.color === "blue"
                        ? isDarkMode
                          ? "text-fsgu-blue-400"
                          : "text-fsgu-blue-600"
                        : stat.color === "gold"
                        ? isDarkMode
                          ? "text-fsgu-gold-400"
                          : "text-fsgu-gold-600"
                        : isDarkMode
                        ? "text-purple-400"
                        : "text-purple-600"
                    }`}
                  />
                </div>
                <ChevronRight
                  className={`w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity ${
                    isDarkMode ? "text-fsgu-silver-500" : "text-fsgu-silver-400"
                  }`}
                />
              </div>

              <div
                className={`text-2xl font-bold mb-0.5 ${
                  isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
                }`}
              >
                {stat.value}
              </div>

              <div
                className={`text-sm font-medium ${
                  isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-600"
                }`}
              >
                {stat.label}
              </div>

              <div
                className={`text-xs mt-1 ${
                  isDarkMode ? "text-fsgu-silver-500" : "text-fsgu-silver-500"
                }`}
              >
                {stat.subtext}
              </div>
            </Link>
          );
        })}
      </div>

      {/* Two Column: Upcoming Tournaments + Recent Results */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Tournaments */}
        <div
          className={`p-6 rounded-xl border ${
            isDarkMode
              ? "bg-fsgu-gray-800 border-fsgu-gray-700"
              : "bg-white border-fsgu-silver-200"
          }`}
        >
          <div className="flex items-center justify-between mb-4">
            <h3
              className={`text-lg font-semibold flex items-center gap-2 ${
                isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
              }`}
            >
              <Calendar className="w-5 h-5 text-fsgu-green-500" />
              Upcoming Tournaments
            </h3>
            <Link
              to="/admin/tournaments"
              className={`text-xs font-medium hover:underline ${
                isDarkMode ? "text-fsgu-green-400" : "text-fsgu-green-600"
              }`}
            >
              View All
            </Link>
          </div>

          {upcomingTournaments.length > 0 ? (
            <div className="space-y-3">
              {upcomingTournaments.map((tournament) => (
                <Link
                  key={tournament.id}
                  to={`/admin/tournaments/${tournament.id}`}
                  className={`block p-3 rounded-lg transition-colors ${
                    isDarkMode
                      ? "bg-fsgu-gray-700/50 hover:bg-fsgu-gray-700"
                      : "bg-fsgu-silver-50 hover:bg-fsgu-silver-100"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <div
                        className={`font-medium truncate ${
                          isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
                        }`}
                      >
                        {tournament.tournament_name}
                      </div>
                      <div
                        className={`text-xs flex items-center gap-2 mt-1 ${
                          isDarkMode ? "text-fsgu-silver-500" : "text-fsgu-silver-500"
                        }`}
                      >
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {tournament.venue_name || tournament.venue_city || "TBA"}
                        </span>
                      </div>
                    </div>
                    <div
                      className={`text-sm font-medium px-2 py-1 rounded ${
                        isDarkMode
                          ? "bg-fsgu-green-500/20 text-fsgu-green-400"
                          : "bg-fsgu-green-100 text-fsgu-green-700"
                      }`}
                    >
                      {formatDate(tournament.start_date)}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Calendar
                className={`w-10 h-10 mx-auto mb-2 ${
                  isDarkMode ? "text-fsgu-silver-600" : "text-fsgu-silver-400"
                }`}
              />
              <p
                className={`text-sm ${
                  isDarkMode ? "text-fsgu-silver-500" : "text-fsgu-silver-500"
                }`}
              >
                No upcoming tournaments
              </p>
              <Link
                to="/admin/tournaments/add"
                className="btn btn-primary btn-sm mt-3 inline-flex items-center"
              >
                <Trophy className="w-4 h-4 mr-1" />
                Add Tournament
              </Link>
            </div>
          )}
        </div>

        {/* Recent Results */}
        <div
          className={`p-6 rounded-xl border ${
            isDarkMode
              ? "bg-fsgu-gray-800 border-fsgu-gray-700"
              : "bg-white border-fsgu-silver-200"
          }`}
        >
          <div className="flex items-center justify-between mb-4">
            <h3
              className={`text-lg font-semibold flex items-center gap-2 ${
                isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
              }`}
            >
              <Award className="w-5 h-5 text-fsgu-gold-500" />
              Recent Results
            </h3>
            <Link
              to="/admin/results"
              className={`text-xs font-medium hover:underline ${
                isDarkMode ? "text-fsgu-green-400" : "text-fsgu-green-600"
              }`}
            >
              View All
            </Link>
          </div>

          {recentResults.length > 0 ? (
            <div className="space-y-3">
              {recentResults.map((result) => (
                <Link
                  key={result.id}
                  to={`/admin/results/edit/${result.id}`}
                  className={`block p-3 rounded-lg transition-colors ${
                    isDarkMode
                      ? "bg-fsgu-gray-700/50 hover:bg-fsgu-gray-700"
                      : "bg-fsgu-silver-50 hover:bg-fsgu-silver-100"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <div
                        className={`font-medium truncate ${
                          isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
                        }`}
                      >
                        {result.tournament_name}
                      </div>
                      <div
                        className={`text-xs flex items-center gap-2 mt-1 ${
                          isDarkMode ? "text-fsgu-silver-500" : "text-fsgu-silver-500"
                        }`}
                      >
                        <Trophy className="w-3 h-3 text-fsgu-gold-500" />
                        {result.winner_name}
                        {result.winner_score && (
                          <span className="opacity-75">({result.winner_score})</span>
                        )}
                      </div>
                    </div>
                    <div
                      className={`text-xs px-2 py-1 rounded ${
                        result.status === "published"
                          ? isDarkMode
                            ? "bg-fsgu-green-500/20 text-fsgu-green-400"
                            : "bg-fsgu-green-100 text-fsgu-green-700"
                          : isDarkMode
                          ? "bg-fsgu-gray-600 text-fsgu-silver-400"
                          : "bg-fsgu-silver-200 text-fsgu-silver-600"
                      }`}
                    >
                      {result.status === "published" ? "Published" : "Draft"}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Award
                className={`w-10 h-10 mx-auto mb-2 ${
                  isDarkMode ? "text-fsgu-silver-600" : "text-fsgu-silver-400"
                }`}
              />
              <p
                className={`text-sm ${
                  isDarkMode ? "text-fsgu-silver-500" : "text-fsgu-silver-500"
                }`}
              >
                No results published yet
              </p>
              <Link
                to="/admin/results/add"
                className="btn btn-primary btn-sm mt-3 inline-flex items-center"
              >
                <Award className="w-4 h-4 mr-1" />
                Add Result
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Back to Top */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className={`fixed bottom-8 right-8 z-50 p-3 rounded-lg shadow-lg transition-all duration-300 hover:scale-110 ${
            isDarkMode
              ? "bg-fsgu-gray-800 hover:bg-fsgu-green-500/20 border-2 border-fsgu-gray-700 hover:border-fsgu-green-500/50"
              : "bg-white hover:bg-fsgu-green-500/10 border-2 border-fsgu-silver-200 hover:border-fsgu-green-500/50"
          }`}
          aria-label="Back to top"
        >
          <ArrowUp
            className={`w-5 h-5 ${
              isDarkMode ? "text-fsgu-green-400" : "text-fsgu-green-500"
            }`}
          />
        </button>
      )}
    </div>
  );
};

export default AdminDashboard;